// Всплывающие определения терминов глоссария.
//
// Разметку выдаёт MarkdownExtensions: у элемента есть data-toggle="popover"
// (или data-bs-toggle), title, data-content / data-bs-content и,
// необязательно, data-bs-footer со ссылкой на глоссарий.
//
// Виджет когда-то давал Bootstrap, позиционирование — Popper. Оба убраны:
// расстановка по четырём сторонам занимает меньше кода, чем весит Popper.
// Классы разметки прежние — их ожидает Component.Popover.scss.

(function () {
    "use strict";

    type Placement = "bottom" | "top" | "right" | "left";

    interface OpenPopover {
        trigger: Element;
        element: HTMLElement;
    }

    const SELECTOR = "[data-toggle=popover], [data-bs-toggle=popover]";

    // Порядок предпочтения сторон и минимальный зазор от края окна.
    const PLACEMENTS: Placement[] = ["bottom", "top", "right", "left"];
    const VIEWPORT_PADDING = 8;

    let open: OpenPopover | null = null;

    function attr(el: Element, name: string): string | null {
        return el.getAttribute("data-bs-" + name) || el.getAttribute("data-" + name);
    }

    function build(trigger: Element): HTMLElement {
        const popover = document.createElement("div");
        popover.className = "popover bs-popover-auto";
        popover.setAttribute("role", "tooltip");

        const title = trigger.getAttribute("data-original-title");
        if (title) {
            const header = document.createElement("h3");
            header.className = "popover-header";
            header.textContent = title;
            popover.appendChild(header);
        }

        const body = document.createElement("div");
        body.className = "popover-body";

        const content = attr(trigger, "content") || "";
        if (attr(trigger, "html") === "true") {
            body.innerHTML = content;
        } else {
            body.textContent = content;
        }
        popover.appendChild(body);

        const footer = attr(trigger, "footer");
        if (footer) {
            const foot = document.createElement("div");
            foot.className = "popover-footer";
            foot.innerHTML = footer;
            popover.appendChild(foot);
        }

        return popover;
    }

    /** Сторона, с которой блок помещается целиком; иначе — где места больше. */
    function choosePlacement(trigger: Element, width: number, height: number): Placement {
        const box = trigger.getBoundingClientRect();

        const space: Record<Placement, number> = {
            bottom: window.innerHeight - box.bottom,
            top: box.top,
            right: window.innerWidth - box.right,
            left: box.left
        };

        for (const side of PLACEMENTS) {
            const needed = side === "top" || side === "bottom" ? height : width;

            if (space[side] >= needed + VIEWPORT_PADDING) {
                return side;
            }
        }

        return PLACEMENTS.reduce(function (best, side) {
            return space[side] > space[best] ? side : best;
        }, PLACEMENTS[0]);
    }

    function place(): void {
        if (!open) {
            return;
        }

        const element = open.element;
        const box = open.trigger.getBoundingClientRect();
        const width = element.offsetWidth;
        const height = element.offsetHeight;
        const side = choosePlacement(open.trigger, width, height);

        let x: number;
        let y: number;

        if (side === "top" || side === "bottom") {
            x = box.left + box.width / 2 - width / 2;
            y = side === "bottom" ? box.bottom : box.top - height;
        } else {
            x = side === "right" ? box.right : box.left - width;
            y = box.top + box.height / 2 - height / 2;
        }

        // Не даём блоку уехать за край окна.
        x = Math.max(VIEWPORT_PADDING, Math.min(x, window.innerWidth - width - VIEWPORT_PADDING));
        y = Math.max(VIEWPORT_PADDING, Math.min(y, window.innerHeight - height - VIEWPORT_PADDING));

        // Координаты окна переводим в систему родителя, относительно
        // которого блок спозиционирован.
        const parent = element.offsetParent || document.body;
        const parentBox = parent.getBoundingClientRect();

        element.setAttribute("x-placement", side);
        element.style.left = (x - parentBox.left + parent.scrollLeft) + "px";
        element.style.top = (y - parentBox.top + parent.scrollTop) + "px";
    }

    function hide(): void {
        if (!open) {
            return;
        }

        if (open.element.parentNode) {
            open.element.parentNode.removeChild(open.element);
        }

        open = null;
    }

    function show(trigger: Element): void {
        hide();

        const element = build(trigger);

        // data-container указывает, куда класть всплывающий блок:
        // в статье это #def-view, иначе — body.
        const selector = attr(trigger, "container");
        const container = (selector && document.querySelector(selector)) || document.body;

        // Размеры нужны до расстановки, поэтому сначала вставляем.
        element.style.left = "0";
        element.style.top = "0";
        container.appendChild(element);

        open = { trigger: trigger, element: element };
        place();
    }

    function init(): void {
        // Свой заголовок вместо системной подсказки браузера.
        document.querySelectorAll(SELECTOR).forEach(function (el) {
            const title = el.getAttribute("title");
            if (title !== null) {
                el.setAttribute("data-original-title", title);
                el.removeAttribute("title");
            }
        });

        document.addEventListener("click", function (e) {
            const target = e.target;

            if (!(target instanceof Element)) {
                return;
            }

            const trigger = target.closest(SELECTOR);

            if (trigger) {
                e.preventDefault();

                if (open && open.trigger === trigger) {
                    hide();
                } else {
                    show(trigger);
                }
                return;
            }

            if (!target.closest(".popover")) {
                hide();
            }
        });

        document.addEventListener("keyup", function (e) {
            if (e.key === "Escape") {
                hide();
            }
        });

        // Открытый блок держится за своим термином.
        window.addEventListener("scroll", place, { passive: true });
        window.addEventListener("resize", place);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init);
    } else {
        init();
    }
})();
